import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { dateToMonDDYYYY, dateTimeToMonDDYYYYWithTime, convertTo12HourFormat } from '../utils/dateUtils';
import ModifyHourSubmittal from './ModifyHourSubmittal';

const FEeventAttendance = ({ userData }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [updatingAttendance, setUpdatingAttendance] = useState(null);
  const [showAttendanceOptions, setShowAttendanceOptions] = useState(null);
  const [conflictNotice, setConflictNotice] = useState(null);
  const [successNotice, setSuccessNotice] = useState(null);
  const [concentrations, setConcentrations] = useState([]);
  const [jobsByEvent, setJobsByEvent] = useState([]);
  const [jobSelections, setJobSelections] = useState({});
  const [showVerifyHoursPopup, setShowVerifyHoursPopup] = useState(false);
  const [selectedRegistrationForVerification, setSelectedRegistrationForVerification] = useState(null);
  const [verifyingHours, setVerifyingHours] = useState(false);
  const [editableHours, setEditableHours] = useState('');
  const [instructors, setInstructors] = useState([]);
  const [userSignature, setUserSignature] = useState(null);
  const [showPDFGeneration, setShowPDFGeneration] = useState(false);
  const [pdfGenerationData, setPdfGenerationData] = useState(null);
  const [studentAssociations, setStudentAssociations] = useState([]);
  const [selectedAssociations, setSelectedAssociations] = useState([]); // Array of association IDs

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        
        const [eventsRes, concentrationsRes, instructorsRes, signatureRes, communityRes] = await Promise.all([
          axios.get('/api/field-experience-events/by-stage/official', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/api/concentrations', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/api/instructors', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/api/user-signature', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/api/user-management/community', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        const filteredEvents = (eventsRes.data.events || []).filter(e => e.status !== 'hidden');
        setEvents(filteredEvents.sort((a, b) => new Date(b.date) - new Date(a.date)));
        setConcentrations(concentrationsRes.data.concentrations || []);
        setInstructors(instructorsRes.data.instructors || []);
        setUserSignature(signatureRes.data.signature);
        setStudentAssociations(communityRes.data.studentAssociations || []);
      } catch (err) {
        setError('Failed to load events.');
        console.error('Error loading events:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleEventChange = (eventId) => {
    setSelectedEventId(eventId);
    setSuccess('');
    setError('');
    setRegistrations([]);
    setShowAttendanceOptions(null);
    setJobSelections({});
    setSelectedAssociations([]);
    if (eventId) {
      fetchRegistrations(eventId);
      fetchJobsByEvent(eventId);
    }
  };

  const fetchRegistrations = async (eventId) => {
    setLoadingRegistrations(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/feeventregistrations/by-event/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrations(res.data.registrations || []);
    } catch (err) {
      console.error('Error fetching registrations:', err);
      setError('Failed to load registrations for this event.');
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const fetchJobsByEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/field-experience-events/jobsbyevent/by-event/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobsByEvent(res.data.jobsbyevent || []);
    } catch (err) {
      setJobsByEvent([]);
    }
  };

  useEffect(() => {
    if (!registrations.length || !jobsByEvent.length) return;
    setJobSelections(prev => {
      const newSelections = { ...prev };
      registrations.forEach(reg => {
        if (reg.job) {
          const jbe = jobsByEvent.find(j => j.id === reg.job);
          if (jbe) {
            newSelections[reg.id] = {
              concentrationId: jbe.concentration,
              jobId: jbe.id
            };
          }
        }
      });
      return newSelections;
    });
  }, [registrations, jobsByEvent]);

  const handleMarkAbsent = async (registrationId) => {
    setUpdatingAttendance(registrationId);
    try {
      const token = localStorage.getItem('token');
      const registration = registrations.find(r => r.id === registrationId);
      
      await axios.put(`/api/feeventregistrations/${registrationId}/attendance`, {
        attended: false,
        attendance: null,
        expectedUpdatedDate: registration?.updateddate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccessNotice({
        registrationId: registrationId,
        message: 'Attendance marked as absent successfully!'
      });
      setTimeout(() => setSuccessNotice(null), 2000);
      setShowAttendanceOptions(null);
      if (selectedEventId) {
        fetchRegistrations(selectedEventId);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setConflictNotice({
          registrationId: registrationId,
          message: 'A change was made while you were on the page. Most recent version has now loaded.'
        });
        setTimeout(() => setConflictNotice(null), 1000);
        if (selectedEventId) {
          fetchRegistrations(selectedEventId);
        }
      } else {
        setError(err.response?.data?.error || 'Failed to update attendance.');
      }
    } finally {
      setUpdatingAttendance(null);
    }
  };

  const handleMarkPresent = async (registrationId, attendanceType) => {
    setUpdatingAttendance(registrationId);
    try {
      const token = localStorage.getItem('token');
      const registration = registrations.find(r => r.id === registrationId);
      
      await axios.put(`/api/feeventregistrations/${registrationId}/attendance`, {
        attended: true,
        attendance: attendanceType,
        expectedUpdatedDate: registration?.updateddate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccessNotice({
        registrationId: registrationId,
        message: `Attendance marked as present (${attendanceType}) successfully!`
      });
      setTimeout(() => setSuccessNotice(null), 2000);
      setShowAttendanceOptions(null);
      if (selectedEventId) {
        fetchRegistrations(selectedEventId);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setConflictNotice({
          registrationId: registrationId,
          message: 'A change was made while you were on the page. Most recent version has now loaded.'
        });
        setTimeout(() => setConflictNotice(null), 1000);
        if (selectedEventId) {
          fetchRegistrations(selectedEventId);
        }
      } else {
        setError(err.response?.data?.error || 'Failed to update attendance.');
      }
    } finally {
      setUpdatingAttendance(null);
    }
  };

  const toggleAttendanceOptions = (registrationId) => {
    setShowAttendanceOptions(showAttendanceOptions === registrationId ? null : registrationId);
  };

  const handleVerifyHours = (registration) => {
    setSelectedRegistrationForVerification(registration);
    const hours = getHoursFromSurveyData(registration);
    setEditableHours(hours ? hours.toString() : '');
    setShowVerifyHoursPopup(true);
  };

  const handleSignAndVerify = async () => {
    if (!selectedRegistrationForVerification) return;
    
    // Validate hours input
    if (!editableHours || isNaN(parseFloat(editableHours))) {
      setError('Please enter a valid number of hours.');
      return;
    }
    
    // Validate signature
    if (!userSignature) {
      setError('User signature not found. Please contact administrator.');
      return;
    }
    
    setVerifyingHours(true);
    try {
      // Prepare data for PDF generation first
      const jobDetails = getJobDetails(selectedRegistrationForVerification);
      if (!jobDetails) {
        throw new Error('Job details not found');
      }
      
      console.log('Registration data for verification:', {
        studentName: selectedRegistrationForVerification.student_name,
        concentration: selectedRegistrationForVerification.concentration,
        concentrationTitle: getConcentrationTitle(selectedRegistrationForVerification.concentration),
        first_name: selectedRegistrationForVerification.first_name,
        last_name: selectedRegistrationForVerification.last_name
      });
      
      const verification = {
        studentName: selectedRegistrationForVerification.student_name || 'Unknown Student',
        eventTitle: selectedEvent?.title || 'Unknown Event',
        eventDate: selectedEvent?.date ? dateToMonDDYYYY(selectedEvent.date, false) : 'TBA',
        hours: parseFloat(editableHours),
        instructorName: getInstructorName(getInstructorFromSurveyData(selectedRegistrationForVerification)),
        instructorSignature: userSignature,
        verifierName: `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || 'Unknown Verifier',
        concentration: getConcentrationTitle(selectedRegistrationForVerification.concentration),
        job: jobDetails,
        folderLocation: `public/events/${selectedEventId}/verifications`,
        filename: `${selectedRegistrationForVerification.id}`,
      };
      
      // Start PDF generation
      setPdfGenerationData(verification);
      setShowPDFGeneration(true);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to prepare verification data.');
    } finally {
      setVerifyingHours(false);
    }
  };

  const getHoursFromSurveyData = (registration) => {
    if (!registration.surveydata) return null;
    
    try {
      const surveyData = Array.isArray(registration.surveydata) 
        ? registration.surveydata 
        : JSON.parse(registration.surveydata);
      
      const hoursQuestion = surveyData.find(q => q._id === 'hours');
      return hoursQuestion && hoursQuestion.response ? parseFloat(hoursQuestion.response) : null;
    } catch (error) {
      console.error('Error parsing survey data:', error);
      return null;
    }
  };

  const getInstructorFromSurveyData = (registration) => {
    if (!registration.surveydata) return null;
    
    try {
      const surveyData = Array.isArray(registration.surveydata) 
        ? registration.surveydata 
        : JSON.parse(registration.surveydata);
      
      const instructorQuestion = surveyData.find(q => q._id === 'instructor');
      return instructorQuestion && instructorQuestion.response ? instructorQuestion.response : null;
    } catch (error) {
      console.error('Error parsing survey data for instructor:', error);
      return null;
    }
  };

  const getInstructorName = (instructorId) => {
    if (!instructorId || !instructors.length) return 'Unknown Instructor';
    const instructor = instructors.find(i => i.account_id === instructorId);
    return instructor ? `${instructor.first_name} ${instructor.last_name}` : 'Unknown Instructor';
  };

  const getJobDetails = (registration) => {
    if (!registration.job || !jobsByEvent.length) return null;
    const job = jobsByEvent.find(j => j.id === registration.job);
    if (!job) return null;
    
    // Map otherConcentrations UUIDs to concentration titles
    const otherConcentrationTitles = [];
    if (Array.isArray(job.otherconcentrations)) {
      job.otherconcentrations.forEach(concId => {
        const concentration = concentrations.find(c => c.id === concId);
        if (concentration) {
          otherConcentrationTitles.push(concentration.title);
        } else {
          // Fallback if concentration not found
          otherConcentrationTitles.push(`Unknown Concentration (${concId})`);
        }
      });
    }
    
    return {
      title: job.title,
      concentration: { title: job.concentration_title || 'Unknown Concentration' },
      otherConcentrations: otherConcentrationTitles,
      description: job.description || 'No description available'
    };
  };

  const handlePDFComplete = async (pdfUrl) => {
    const token = localStorage.getItem('token');
    
    try {
      console.log('Sending verify-hours-complete request:', {
        registrationId: selectedRegistrationForVerification.id,
        expectedUpdatedDate: selectedRegistrationForVerification.updateddate,
        hours: parseFloat(editableHours),
        pdflink: pdfUrl,
        currentTime: new Date().toISOString()
      });
      
      const verifyResponse = await axios.put(`/api/feeventregistrations/${selectedRegistrationForVerification.id}/verify-hours-complete`, {
        expectedUpdatedDate: selectedRegistrationForVerification.updateddate,
        hours: parseFloat(editableHours),
        pdflink: pdfUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Verify-hours-complete response received:', {
        registrationId: verifyResponse.data.registration.id,
        newUpdatedDate: verifyResponse.data.registration.updateddate,
        newStatus: verifyResponse.data.registration.status,
        newHours: verifyResponse.data.registration.hours,
        newVerifier: verifyResponse.data.registration.verifier,
        newPdflink: verifyResponse.data.registration.pdflink,
        responseTime: new Date().toISOString()
      });
      
      setSuccessNotice({
        registrationId: selectedRegistrationForVerification.id,
        message: 'Hours verified and PDF generated successfully!'
      });
      setTimeout(() => setSuccessNotice(null), 2000);
      setShowPDFGeneration(false);
      setPdfGenerationData(null);
      setShowVerifyHoursPopup(false);
      setSelectedRegistrationForVerification(null);
      setEditableHours('');
      
      // Refresh the registration list to get the most current data
      if (selectedEventId) {
        console.log('Refreshing registration list after successful verification');
        await fetchRegistrations(selectedEventId);
      }
    } catch (err) {
      console.error('Error completing hours verification:', err);
      if (err.response?.status === 409) {
        setConflictNotice({
          registrationId: selectedRegistrationForVerification.id,
          message: 'A change was made while you were on the page. Most recent version has now loaded.'
        });
        setTimeout(() => setConflictNotice(null), 1000);
        if (selectedEventId) {
          console.log('Refreshing registration list after 409 conflict');
          await fetchRegistrations(selectedEventId);
        }
      } else {
        setError(err.response?.data?.error || 'Failed to complete hours verification.');
      }
      setShowPDFGeneration(false);
      setPdfGenerationData(null);
    }
  };

  const handlePDFError = (error) => {
    setError(`PDF generation failed: ${error}`);
    setShowPDFGeneration(false);
    setPdfGenerationData(null);
  };

  const getAttendanceStatus = (registration) => {
    if (registration.attended === null) {
      return { text: 'Not Marked', color: '#6c757d' };
    } else if (registration.attended === false) {
      return { text: '❌Absent', color: '#dc3545' };
    } else {
      return { 
        text: `✅Present (${registration.attendance})`, 
        color: '#28a745' 
      };
    }
  };

  const getConcentrationTitle = (concentrationId) => {
    if (!concentrationId || !concentrations.length) return 'N/A';
    const concentration = concentrations.find(c => c.id === concentrationId);
    return concentration ? concentration.title : 'N/A';
  };

  const handleCopyEmails = async () => {
    if (registrations.length === 0) {
      alert('No registrations to copy emails from.');
      return;
    }

    // Extract emails from registrations, filtering out null/undefined/empty emails
    const emails = registrations
      .map(reg => reg.primary_email)
      .filter(email => email && email.trim() !== '')
      .filter((email, index, self) => self.indexOf(email) === index); // Remove duplicates

    if (emails.length === 0) {
      alert('No email addresses found in registrations.');
      return;
    }

    // Join emails with newline
    const emailString = emails.join('\n');

    try {
      await navigator.clipboard.writeText(emailString);
      setSuccess(`Copied ${emails.length} email(s) to clipboard!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to copy emails:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = emailString;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setSuccess(`Copied ${emails.length} email(s) to clipboard!`);
        setTimeout(() => setSuccess(''), 3000);
      } catch (fallbackErr) {
        setError('Failed to copy emails to clipboard.');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleCopyRegistrationId = async (registrationId) => {
    try {
      await navigator.clipboard.writeText(registrationId);
      setSuccess(`Copied registration ID: ${registrationId}`);
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy registration ID:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = registrationId;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setSuccess(`Copied registration ID: ${registrationId}`);
        setTimeout(() => setSuccess(''), 2000);
      } catch (fallbackErr) {
        setError('Failed to copy registration ID to clipboard.');
      }
      document.body.removeChild(textArea);
    }
  };

  // Calculate associations present in registrations with counts
  const associationsWithCounts = useMemo(() => {
    const counts = {};
    registrations.forEach(reg => {
      if (reg.association_id && reg.association_title) {
        counts[reg.association_id] = {
          id: reg.association_id,
          title: reg.association_title,
          count: (counts[reg.association_id]?.count || 0) + 1
        };
      }
    });
    return Object.values(counts).sort((a, b) => a.title.localeCompare(b.title));
  }, [registrations]);

  // Filter registrations by selected associations
  const filteredRegistrations = useMemo(() => {
    if (selectedAssociations.length === 0) return registrations;
    return registrations.filter(reg => selectedAssociations.includes(reg.association_id));
  }, [registrations, selectedAssociations]);

  // Toggle association selection
  const toggleAssociation = (associationId) => {
    setSelectedAssociations(prev => {
      if (prev.includes(associationId)) {
        return prev.filter(id => id !== associationId);
      } else {
        return [...prev, associationId];
      }
    });
  };

  // CSV download functionality
  const handleDownloadCSV = () => {
    if (filteredRegistrations.length === 0) {
      alert('No registrations to export.');
      return;
    }

    // Prepare CSV data
    const csvData = filteredRegistrations.map(reg => {
      const attendanceStatus = getAttendanceStatus(reg);
      return {
        studentName: reg.student_name || 'Unknown Student',
        email: reg.primary_email || '',
        association: reg.association_title || 'N/A',
        concentration: getConcentrationTitle(reg.concentration),
        gradeLevel: reg.gradelevel || 'N/A',
        inPersonDesired: reg.inpersondesired ? 'Yes' : 'No',
        attendanceStatus: attendanceStatus.text,
        attendanceType: reg.attendance || 'N/A',
        hours: reg.hours || 'N/A',
        status: reg.status || 'Pending',
        registrationId: reg.id
      };
    });

    // Convert to CSV format
    const headers = ['Student Name', 'Email', 'Association', 'Concentration', 'Grade Level', 'In Person Desired', 'Attendance Status', 'Attendance Type', 'Hours', 'Status', 'Registration ID'];
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => [
        `"${row.studentName}"`,
        `"${row.email}"`,
        `"${row.association}"`,
        `"${row.concentration}"`,
        `"${row.gradeLevel}"`,
        `"${row.inPersonDesired}"`,
        `"${row.attendanceStatus}"`,
        `"${row.attendanceType}"`,
        row.hours === 'N/A' ? 'N/A' : row.hours,
        `"${row.status}"`,
        `"${row.registrationId}"`
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const eventTitle = selectedEvent ? selectedEvent.title.replace(/[^a-z0-9]/gi, '-').toLowerCase() : 'event';
    link.setAttribute('download', `attendance-${eventTitle}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSuccess('CSV downloaded successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };


  const selectedEvent = events.find(e => e.id === selectedEventId);

  if (loading) return <LoadingSpinner />;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>FE Event Attendance</h1>
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}
      
      <div style={styles.formContainer}>
        <div style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Select Event</h2>
          <select 
            value={selectedEventId} 
            onChange={(e) => handleEventChange(e.target.value)}
            style={styles.select}
          >
            <option value="">Choose an event...</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
               {event.date ? dateToMonDDYYYY(event.date, false) : 'No Date'} - {event.title} ({event.type})
              </option>
            ))}
          </select>
        </div>

        {selectedEvent && (
          <div style={styles.eventDetails}>
            <h2 style={styles.sectionTitle}>Event Details</h2>
            <div style={styles.detailsGrid}>
              <div><strong>Title:</strong> {selectedEvent.title}</div>
              <div><strong>Date:</strong> {selectedEvent.date ? dateToMonDDYYYY(selectedEvent.date, false) : 'TBA'}</div>
              <div><strong>Time:</strong> {convertTo12HourFormat(selectedEvent.start_time) || 'TBA'} - {convertTo12HourFormat(selectedEvent.end_time) || 'TBA'}</div>
              <div><strong>Location:</strong> {selectedEvent.location || 'TBA'}</div>
              <div><strong>Type:</strong> {selectedEvent.type}</div>
              <div><strong>Hours:</strong> {selectedEvent.hoursavailable || 'N/A'}</div>
            </div>
          </div>
        )}

        {selectedEvent && (
          <div style={styles.registrationsSection}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <h2 style={styles.sectionTitle}>
                  Attendance ({filteredRegistrations.length}{selectedAssociations.length > 0 ? ` / ${registrations.length}` : ''})
                  {loadingRegistrations && <span style={styles.loadingText}> - Loading...</span>}
                </h2>
                
                {filteredRegistrations.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      onClick={handleCopyEmails}
                      style={{
                        padding: '0.375rem',
                        backgroundColor: 'transparent',
                        color: '#6c757d',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '1.1rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#495057';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#6c757d';
                        e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)';
                      }}
                      title="Copy all student emails to clipboard"
                    >
                      📋
                    </button>
                    <button
                      onClick={handleDownloadCSV}
                      style={{
                        padding: '0.375rem',
                        backgroundColor: 'transparent',
                        color: '#6c757d',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '1.1rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#495057';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#6c757d';
                        e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)';
                      }}
                      title="Download CSV file"
                    >
                      ⬇️
                    </button>
                  </div>
                )}
              </div>
              
              {/* Association Filter Tabs */}
              {associationsWithCounts.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  alignItems: 'center'
                }}>
                  {associationsWithCounts.map(assoc => {
                    const isSelected = selectedAssociations.includes(assoc.id);
                    return (
                      <button
                        key={assoc.id}
                        onClick={() => toggleAssociation(assoc.id)}
                        style={{
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.75rem',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: isSelected ? '#007bff' : 'transparent',
                          color: isSelected ? 'white' : '#007bff',
                          fontWeight: '500',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.target.style.backgroundColor = '#f0f9ff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.target.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {assoc.title} ({assoc.count})
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            {filteredRegistrations.length === 0 ? (
              <div style={styles.noRegistrations}>
                {registrations.length === 0 
                  ? 'No registrations for this event yet.'
                  : `No registrations match the selected association filter.`}
              </div>
            ) : (
              <div style={styles.registrationsList}>
                {filteredRegistrations.map((registration) => {
                  const attendanceStatus = getAttendanceStatus(registration);
                  const isUpdating = updatingAttendance === registration.id;
                  const showOptions = showAttendanceOptions === registration.id;
                  const showConflictNotice = conflictNotice && conflictNotice.registrationId === registration.id;
                  const showSuccessNotice = successNotice && successNotice.registrationId === registration.id;
                  const isVerified = registration.status === 'Verified';

                  return (
                    <div
                      id={`registration-item-${registration.id}`}
                      style={{
                        ...styles.registrationItemOuter,
                        ...(isVerified
                          ? {
                              backgroundColor: '#e6f9ed',
                              border: '2px solid #28a745',
                            }
                          : {}),
                      }}
                      key={registration.id}
                      onDoubleClick={() => handleCopyRegistrationId(registration.id)}
                      title="Double-click to copy registration ID"
                    >
                      <div style={styles.registrationHeaderRow}>
                        <div style={styles.studentName}>{registration.student_name || 'Unknown Student'}</div>
                        <div style={styles.registrationHeaderInfo}>
                          <span><strong>Association:</strong> {registration.association_title || 'N/A'}</span>
                          <span><strong>In Person Desired:</strong> {registration.inpersondesired ? 'Yes' : 'No'}</span>
                          <span><strong>Grade Level:</strong> {registration.gradelevel || 'N/A'}</span>
                          <span><strong>Concentration:</strong> {getConcentrationTitle(registration.concentration)}</span>
                        </div>
                      </div>
                      <hr style={styles.registrationDivider} />
                      <div style={{
                        ...styles.registrationGrid,
                        ...(isUpdating ? { display: 'none' } : {})
                      }}>
                        <div style={styles.registrationColBoxed}>
                          <div style={styles.attendanceStatus}>
                            <span style={{...styles.statusText, color: attendanceStatus.color}}>
                              {attendanceStatus.text}
                            </span>
                          </div>
                          <div style={styles.attendanceControlsBox}>
                            {!showOptions ? (
                              <div style={styles.buttonGroupCentered}>
                                <button
                                  style={styles.presentButton}
                                  onClick={() => toggleAttendanceOptions(registration.id)}
                                  disabled={isUpdating || isVerified}
                                >
                                  Present?
                                </button>
                                <button
                                  style={styles.absentButton}
                                  onClick={() => handleMarkAbsent(registration.id)}
                                  disabled={isUpdating || registration.attended === false || isVerified}
                                >
                                  Absent?
                                </button>
                              </div>
                            ) : (
                              <div style={styles.attendanceOptions}>
                                <button
                                  style={styles.onsiteButton}
                                  onClick={() => handleMarkPresent(registration.id, 'Onsite')}
                                  disabled={isUpdating || (registration.attended === true && registration.attendance === 'Onsite') || isVerified}
                                >
                                  In Person
                                </button>
                                <button
                                  style={styles.virtualButton}
                                  onClick={() => handleMarkPresent(registration.id, 'Virtual')}
                                  disabled={isUpdating || (registration.attended === true && registration.attendance === 'Virtual') || isVerified}
                                >
                                  Virtual
                                </button>
                                <button
                                  style={styles.cancelButton}
                                  onClick={() => setShowAttendanceOptions(null)}
                                  disabled={isUpdating || isVerified}
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                            {isUpdating && <span style={styles.updatingText}>Updating...</span>}
                          </div>
                        </div>
                        <div style={styles.registrationColBoxed}>
                          <div style={styles.dropdownRowGroup}>
                            <div style={styles.dropdownGroupInline}>
                              <label style={styles.dropdownLabelInline} htmlFor={`concentration-dropdown-${registration.id}`}>Concentration</label>
                              <select
                                id={`concentration-dropdown-${registration.id}`}
                                style={styles.dropdownSelectInline}
                                value={jobSelections[registration.id]?.concentrationId || ''}
                                onChange={e => {
                                  const concentrationId = e.target.value;
                                  setJobSelections(prev => ({
                                    ...prev,
                                    [registration.id]: {
                                      ...prev[registration.id],
                                      concentrationId,
                                    },
                                  }));
                                }}
                                disabled={isVerified}
                              >
                                <option value="">Select...</option>
                                {Array.from(new Set(jobsByEvent
                                  .filter(jbe => jbe.status === 'event')
                                  .map(jbe => jbe.concentration)))
                                  .map(concId => {
                                    const conc = concentrations.find(c => c.id === concId);
                                    return conc ? (
                                      <option key={conc.id} value={conc.id}>{conc.title}</option>
                                    ) : null;
                                  })}
                              </select>
                            </div>
                            <div style={styles.dropdownGroupInline}>
                              <label style={styles.dropdownLabelInline} htmlFor={`job-dropdown-${registration.id}`}>Job</label>
                              <select
                                id={`job-dropdown-${registration.id}`}
                                style={styles.dropdownSelectInline}
                                value={jobSelections[registration.id]?.jobId || ''}
                                onChange={async e => {
                                  const jobId = e.target.value;
                                  if (jobId) {
                                    setUpdatingAttendance(registration.id);
                                    try {
                                      const token = localStorage.getItem('token');
                                      await axios.patch(`/api/feeventregistrations/${registration.id}`, {
                                        job: jobId,
                                        expectedUpdatedDate: registration.updateddate
                                      }, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      setSuccessNotice({
                                        registrationId: registration.id,
                                        message: 'Job updated!'
                                      });
                                      setTimeout(() => setSuccessNotice(null), 1500);
                                      if (selectedEventId) fetchRegistrations(selectedEventId);
                                    } catch (err) {
                                      if (err.response?.status === 409) {
                                        setConflictNotice({
                                          registrationId: registration.id,
                                          message: 'A change was made while you were on the page. Most recent version has now loaded.'
                                        });
                                        setTimeout(() => setConflictNotice(null), 1000);
                                        if (selectedEventId) fetchRegistrations(selectedEventId);
                                      } else {
                                        setError('Failed to update job for registration.');
                                      }
                                    } finally {
                                      setUpdatingAttendance(null);
                                    }
                                  }
                                }}
                                disabled={!jobSelections[registration.id]?.concentrationId || isUpdating || isVerified}
                              >
                                <option value="">Select...</option>
                                {jobsByEvent
                                  .filter(jbe => jbe.status === 'event' && jbe.concentration === jobSelections[registration.id]?.concentrationId)
                                  .map(jbe => (
                                    <option key={jbe.id} value={jbe.id}>{jbe.title}</option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        <div style={styles.registrationColBoxed}>
                          {registration.status === 'Pending' && (
                            <div style={styles.verifyHoursSection}>
                              <button
                                style={styles.verifyHoursButton}
                                onClick={() => handleVerifyHours(registration)}
                                disabled={isUpdating || isVerified}
                              >
                                Verify Hours
                              </button>
                            </div>
                          )}
                          {registration.status === 'Verified' && (
                            <div style={styles.verifiedHoursSection}>
                              <div style={styles.verifiedHoursText}>
                                ✅ {registration.hours} Hours Verified
                                {registration.pdflink && (
                                  <a
                                    href={registration.pdflink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.pdfLink}
                                    title="Open verification PDF"
                                  >
                                    🔗
                                  </a>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{
                        ...styles.noticeContainer,
                        ...(showConflictNotice || showSuccessNotice ? { minHeight: '20px' } : {})
                      }}>
                        {showConflictNotice && (
                          <div style={styles.conflictNotice}>
                            {conflictNotice.message}
                          </div>
                        )}
                        {showSuccessNotice && (
                          <div style={styles.successNotice}>
                            {successNotice.message}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* PDF Generation Modal */}
      {showPDFGeneration && pdfGenerationData && (
        <ModifyHourSubmittal
          verification={pdfGenerationData}
          onComplete={handlePDFComplete}
          onError={handlePDFError}
        />
      )}

      {/* Verify Hours Popup */}
      {showVerifyHoursPopup && selectedRegistrationForVerification && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupContent}>
            <div style={styles.popupHeader}>
              <h3 style={styles.popupTitle}>Verify Hours</h3>
                              <button
                  style={styles.popupCloseButton}
                  onClick={() => {
                    setShowVerifyHoursPopup(false);
                    setSelectedRegistrationForVerification(null);
                    setEditableHours('');
                  }}
                >
                ×
              </button>
            </div>
            <div style={styles.popupBody}>
              <div style={styles.studentInfo}>
                <strong>Student:</strong> {selectedRegistrationForVerification.student_name || 'Unknown Student'}
              </div>
              <div style={styles.instructorInfo}>
                <strong>Instructor:</strong> {getInstructorName(getInstructorFromSurveyData(selectedRegistrationForVerification))}
              </div>
              <div style={styles.hoursInputSection}>
                <label style={styles.hoursInputLabel} htmlFor="hours-input">
                  <strong>Hours to Verify:</strong>
                </label>
                <input
                  id="hours-input"
                  type="number"
                  step="0.1"
                  min="0"
                  value={editableHours}
                  onChange={(e) => setEditableHours(e.target.value)}
                  style={styles.hoursInput}
                  placeholder="Enter hours"
                  disabled={verifyingHours}
                />
              </div>
              <div style={styles.popupButtons}>
                <button
                  style={styles.signAndVerifyButton}
                  onClick={handleSignAndVerify}
                  disabled={verifyingHours}
                >
                  {verifyingHours ? 'Verifying...' : 'Sign & Verify'}
                </button>
                <button
                  style={styles.cancelVerifyButton}
                  onClick={() => {
                    setShowVerifyHoursPopup(false);
                    setSelectedRegistrationForVerification(null);
                    setEditableHours('');
                  }}
                  disabled={verifyingHours}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  title: {
    fontSize: '2rem',
    marginBottom: '2rem',
    color: '#333',
    textAlign: 'center'
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem'
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  sectionTitle: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#1D4E89',
    marginBottom: '0.5rem'
  },
  select: {
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  eventDetails: {
    background: '#f8f9fa',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    fontSize: '1rem'
  },
  registrationsSection: {
    background: '#f8f9fa',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  loadingText: {
    fontSize: '0.8rem',
    color: '#6c757d',
    marginLeft: '0.5rem'
  },
  noRegistrations: {
    padding: '1rem',
    color: '#6c757d',
    textAlign: 'center'
  },
  registrationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  registrationItemOuter: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0.25rem',
    alignItems: 'center',
    padding: '0.5rem',
    backgroundColor: '#fff',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    marginBottom: '0px',
    position: 'relative'
  },
  registrationHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1rem',
    marginBottom: '0',
    padding: 0
  },
  registrationHeaderInfo: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1.1rem',
    fontSize: '0.75rem',
    color: '#6c757d',
    fontWeight: 500,
    alignItems: 'center',
    marginLeft: '1rem'
  },
  studentName: {
    fontWeight: '600',
    color: '#333',
    fontSize: '1.08rem',
    marginBottom: 0
  },
  attendanceStatus: {
    textAlign: 'center',
    marginBottom: '0.1rem'
  },
  statusText: {
    fontWeight: '600',
    fontSize: '1rem'
  },
  attendanceControlsBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
    minHeight: 0,
    padding: 0
  },
  buttonGroupCentered: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.25rem'
  },
  presentButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  absentButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  attendanceOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    alignItems: 'center'
  },
  onsiteButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    width: '100%'
  },
  virtualButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#6f42c1',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    width: '100%'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    width: '100%'
  },
  updatingText: {
    fontSize: '0.8rem',
    color: '#6c757d',
    fontStyle: 'italic'
  },
  error: {
    padding: '1rem',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #f5c6cb'
  },
  success: {
    padding: '1rem',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #c3e6cb'
  },
  noticeContainer: {
    position: 'relative',
    height: '0',
    overflow: 'visible'
  },
  conflictNotice: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    padding: '0.5rem',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    border: '1px solid #f5c6cb',
    zIndex: 1,
    textAlign: 'center'
  },
  successNotice: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    padding: '0.5rem',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '4px',
    border: '1px solid #c3e6cb',
    zIndex: 1,
    textAlign: 'center'
  },
  registrationDivider: {
    border: 'none',
    borderTop: '1px solid #e0e0e0',
    margin: '0.5rem 0'
  },
  registrationGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '0.5rem',
    alignItems: 'start',
    minHeight: 0
  },
  registrationColBoxed: {
    padding: '0.25rem',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    minHeight: 0
  },
  dropdownRowGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.5rem',
    width: '100%'
  },
  dropdownGroupInline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
    marginBottom: '0.2rem',
    flex: 1,
    minWidth: 0
  },
  dropdownLabelInline: {
    fontWeight: '600',
    color: '#333',
    fontSize: '0.88rem',
    marginBottom: '0.05rem'
  },
  dropdownSelectInline: {
    padding: '0.25rem',
    fontSize: '0.97rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    minHeight: 0,
    width: '100%'
  },
  verifyHoursSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '0.5rem'
  },
  verifyHoursButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#ffc107',
    color: '#212529',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    width: '100%'
  },
  verifiedHoursSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '0.5rem'
  },
  verifiedHoursText: {
    fontSize: '0.9rem',
    color: '#28a745',
    fontWeight: '600',
    textAlign: 'center'
  },
  pdfLink: {
    marginLeft: '0.5rem',
    textDecoration: 'none',
    color: '#007bff',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'color 0.3s ease'
  },
  popupOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  popupContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto'
  },
  popupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e0e0e0'
  },
  popupTitle: {
    margin: 0,
    fontSize: '1.3rem',
    color: '#333'
  },
  popupCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666',
    padding: '0.25rem',
    borderRadius: '4px',
    transition: 'background-color 0.3s ease'
  },
  popupBody: {
    padding: '1.5rem'
  },
  studentInfo: {
    marginBottom: '1rem',
    fontSize: '1rem'
  },
  instructorInfo: {
    marginBottom: '1rem',
    fontSize: '1rem'
  },
  hoursInputSection: {
    marginBottom: '1.5rem'
  },
  hoursInputLabel: {
    display: 'block',
    marginBottom: '0.5rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1D4E89'
  },
  hoursInput: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#fff'
  },
  popupButtons: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end'
  },
  signAndVerifyButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  cancelVerifyButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  }
};

export default FEeventAttendance;